import { API_BASE_URL } from 'config';
import {
  Login,
  LoginContext,
  LoginOrganizationInfo,
  LoginUserDetailDto,
  LoginUserListItemDto,
  NexosApiResponse,
} from '../types';

// Maps organization type string from the Nexos current-user endpoints to the numeric ID
// used in legacy LoginContext. designer = 1, supplier = 2 (matches OrganizationTypeId in
// DesignHub.Domain)
const ORG_TYPE_ID: Record<string, number> = { designer: 1, supplier: 2 };

const mapOrganizationToLoginInfo = (
  organization: LoginUserDetailDto['organization']
): LoginOrganizationInfo => ({
  address: organization.address,
  zipCode: organization.zipCode,
  city: organization.city,
  stateOrRegion: organization.stateOrRegion,
  website: organization.website,
  phoneNumber: organization.phoneNumber,
  faxNumber: organization.faxNumber,
  vatNumber: organization.vatNumber,
  localCurrency: organization.localCurrency,
  country: organization.country,
});

const mapLoginUserDetailToLogin = (user: LoginUserDetailDto): Login => {
  if (!user) return null;
  const loginContext: LoginContext = {
    id: user.id,
    name: user.name,
    organizationType: ORG_TYPE_ID[user.organization.type] ?? 0,
    organizationName: user.organization.name,
    email: user.email,
    roleNames: user.roles.map(role => role.name),
    designerOrganizationName: user.organization.name,
    designerOrganizationId: user.organization.id,
    brands: undefined,
    organizationId: user.organization.id,
    signUpDate: undefined,
    planType: undefined,
    planTier: undefined,
    platformNewsletterSubscription: undefined,
    inviteCode: user.inviteCode ?? undefined,
    organizationInfo: mapOrganizationToLoginInfo(user.organization),
    // The org's active modules gate D2 nav/features (features/accessControl useModule);
    // under federated auth this endpoint is their only source — /api/user (legacy Api2)
    // is not called.
    modules: user.modules ?? [],
    avatarColor: user.avatarColor,
  };
  return { loginContext, user: null, token: null };
};

export const fetchLoginUsers = async (accessToken: string): Promise<LoginUserListItemDto[]> => {
  if (!accessToken) return [];
  const response = await fetch(`${API_BASE_URL}/api/users/me/logins`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch login users: ${response.status} ${errorText}`);
  }
  const body: NexosApiResponse<LoginUserListItemDto[]> = await response.json();
  return body.data;
};

export const fetchCurrentLoginUser = async (accessToken: string): Promise<Login> => {
  if (!accessToken) return null;
  const response = await fetch(`${API_BASE_URL}/api/users/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch current user: ${response.status} ${errorText}`);
  }
  const body: NexosApiResponse<LoginUserDetailDto> = await response.json();
  return mapLoginUserDetailToLogin(body.data);
};
